import BaseAPIClient from './base/BaseAPIClient';
import ListResponseModel from '../models/ListResponseModel.js';
import CounselorRequestModel from '../models/CounselorRequestModel.js';
import CounselorProfileModel from '../models/CounselorProfileModel.js';
import CounselorAvailabilityModel from '../models/CounselorAvailabilityModel.js';
import CollegeModel from '../models/CollegeModel.js';
import AppointmentModel from '../models/AppointmentModel.js';
import CampusEventModel from '../models/CampusEventModel.js';
import GroupPollModel from '../models/GroupPollModel.js';
import AppointmentRequestModel from '../models/AppointmentRequestModel.js';
import RecommendedActionModel from '../models/RecommendedActionModel.js';
import CounselorGroupSessionModel from '../models/CounselorGroupSessionModel.js';
import PaymentModel from '../models/PaymentModel.js';
import CounselingPreferencesModel from '../models/CounselingPreferencesModel.js';
import moment from 'moment';


export default class StudentClient extends BaseAPIClient {
  // student dashboard
  getRecommendedActions() {
    return this._get('counseling_sessions/recommended_action/').then(m => new RecommendedActionModel(m));
  }

  getRecommendedCounselors(studentUserId, recordsPerPage = 3, nextAvailableAfter) {
    return this._get('v2/campus-administration/student/recommended-counselors/', {
      studentUserId,
      recordsPerPage,
      pageToken: 'PreferenceScoreDescendingPageToken',
      nextAvailableAfter,
    });
  }

  getCounselorPreferences() {
    return this._get('users/me/counseling_preferences/').then(m => new CounselingPreferencesModel(m));
  }

  async setCounselorPreferences(data) {
    await this._patch('users/me/counseling_preferences/', data);
  }

  getCounselors(studentUserId, pageToken, filters, recordsPerPage = 10) {
    return this._get('v2/campus-administration/student/recommended-counselors/', {
      studentUserId,
      recordsPerPage,
      pageToken: (pageToken || 'PreferenceScoreDescendingPageToken'),
      ...filters
    });
  }

  updatePreferences(studentUserId) {
    return this._post('v2/preference/student/', { studentUserId })
  }

  getUpcomingJoinedSessions(limit = 10, offset = 0) {
    const data = {
      limit,
      offset,
      type: 'group_session',
      attending_only: true,
      start_time_after: moment().subtract(2, 'hour').toISOString(),
    };

    return this._get('counseling_sessions/group_sessions/', data).then(m => new ListResponseModel(m, CounselorGroupSessionModel));
  }

  getPrimaryCounselor(clientUserId) {
    return this._get('v2/campus-administration/student/primary-counselor/', { clientUserId });
  }

  getCounselorAvailabilityQuery(counselorId, week = 0) {
    return this._post(`counselors/counselors/${counselorId}/availability_query/`, {
      week,
    }).then(m => (m.days || []).map(m => new CounselorAvailabilityModel(m)));
  }

  // getCurrentGroupPoll() {
  //   // get current group poll i think cody said is not a real thing that was actually defined or discussed so i think we can leave that out.
  //   return this._get('').then(r => new GroupPollModel(r));
  // }

  getMyScheduledAppointments(limit = 10, offset = 0) {
    return this._get('counseling_sessions/students/my_appointments/', {
      limit,
      offset,
      end_time_after: new Date().toISOString(),
      ordering: 'start_time',
    }).then(m => new ListResponseModel(m, AppointmentModel));
  }

  getMyAppointmentRequests(limit = 10, offset = 0) {
    return this._get('counseling_sessions/students/my_appointment_requests/', {
      pending: true,
      limit,
      offset,
    }).then(m => new ListResponseModel(m, AppointmentRequestModel));
  }

  getMyRecentAppointments(start_time, limit = 10, offset = 0) {
    const now = new Date();
    const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    return this._get('counseling_sessions/students/my_appointments/', {
      limit,
      offset,
      start_time_after: lastWeek.toISOString(),
      start_time_before: now.toISOString(),
      ordering: '-start_time',
    }).then(m => new ListResponseModel(m, AppointmentModel));
  }

  submitCounselorSessionRequest(counselor_id, start_time, end_time, auto_pay_with_credit) {
    return this._post('counseling_sessions/requests/', {
      counselor_id,
      start_time,
      end_time,
      auto_pay_with_credit,
    });
  }

  patchMyAppointmentRequestsDetail(id, approved) {
    return this._patch(`counseling_sessions/my_appointment_requests/${id}/`, {
      approved,
    }).then(m => new AppointmentRequestModel(m));
  }


  getMyPayments(status = '', limit = 10, offset = 0) {
    return this._get('counseling_sessions/payments/', {
      status, // pending|paid
      limit,
      offset,
    }).then(m => new ListResponseModel(m, PaymentModel));
  }

  getPaymentDetail(id) {
    return this._get(`counseling_sessions/payments/${id}/`).then(r => new PaymentModel(r));
  }

  getRemainingCredits() {
    return this._get('counseling_sessions/credits/').then(res => ({
        individual_session_credits_remaining: res.individual_session_credits_remaining || 0,
        group_session_credits_remaining: res.group_session_credits_remaining || 0,
      }));
  }

  // use credit for a payment
  payForSessionWithCredit(id) {
    return this._post(`counseling_sessions/payments/${id}/use_credit/`);
  }

  // pay for a payment with Stripe
  payForSessionWithStripe(id, success_url, cancel_url) {
    return this._post(`counseling_sessions/payments/${id}/checkout/`, {
      success_url,
      cancel_url,
    });
  }

  getGroupSessions(filters, limit = 10, offset = 0) {
    const data = filters || {};
    data.limit = limit;
    data.offset = offset;
    data.type = 'group_session';
    data.ordering = 'start_time';
    if (!data.start_time_after) {
      data.start_time_after = moment().subtract(1, 'days').toISOString();
    }

    return this._get('counseling_sessions/group_sessions/', data).then(m => new ListResponseModel(m, CounselorGroupSessionModel));
  }

  joinGroupSession(id, auto_pay_with_credit) {
    return this._post(`counseling_sessions/group_sessions/${id}/join/`, {
      auto_pay_with_credit,
    });
  }


  getGroupEvents(filters, limit = 10, offset = 0) {
    const data = filters || {};
    data.type = 'college_event';
    data.limit = limit;
    data.offset = offset;
    if (!data.start_time_after) {
      data.start_time_after = moment().subtract(1, 'day').toISOString();
    }

    return this._get('counseling_sessions/group_sessions/', data).then(m => new ListResponseModel(m, CounselorGroupSessionModel));
  }

  joinGroupEvent(id) {
    return this._post(`counseling_sessions/group_sessions/${id}/join/`, {
      type: 'college_event',
    });
  }

  getProfile(studentUserId) {
    return this._get('v2/campus-administration/students/profile/', { studentUserId });
  }

  campusSearch(nameContains, emailDomainEquals) {
    return this._get(
      'v2/campus-administration/student/registration/campus-search/',
      { nameContains, emailDomainEquals });
  }

  getCounselorProfile(counselorId) {
    return this._get('v2/campus-administration/student/counselor-profile/', { counselorId });
  }
}
